import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { ProfileInvitation } from '../data-models/profile-invitation.model';
import { ProfileInvitationRequest } from '../request-models/profile-invitation-request.model';
import { InvitationEmailResult } from '../response-models/invitation-email-result.model';
import { InvitationResendResult } from '../response-models/invitation-resend-result.model';
import { ProfileInvitationCreateResponse } from '../response-models/profile-invitation-create-response.model';
import { toInvitationEmailResult, toInvitationResendResult } from '../utils/invitation-result-utils';
import { isRecord } from '../utils/is-record';
import { idString, nullableString, omitUndefined } from '../utils/wire-value-utils';

/**
 * Admin invitations: `/admin/invitations` (hub `admin-invitations-controller.ts`). Creating one also creates the
 * Firebase account, profile and membership. Resend, send-email, cancel and delete accept either a profile or an
 * organization invitation id (the hub tries both).
 */
@Injectable({ providedIn: 'root' })
export class AdminInvitationService extends BaseService<ProfileInvitation> {
  constructor() {
    super('admin/invitations', ProfileInvitation);
  }

  /** POST /admin/invitations */
  createInvitation(body: ProfileInvitationRequest): Observable<ProfileInvitationCreateResponse> {
    return this.request('POST', {}, toCreateResponse, toInvitationRequest(body));
  }

  /** POST /admin/invitations/:id/resend */
  resendInvitation(invitationId: string): Observable<InvitationResendResult> {
    return this.request('POST', { id: invitationId, path: 'resend' }, toInvitationResendResult, {});
  }

  /** POST /admin/invitations/:id/send-email: the first send of an invitation created without email. */
  sendInvitationEmail(invitationId: string): Observable<InvitationEmailResult> {
    return this.request('POST', { id: invitationId, path: 'send-email' }, toInvitationEmailResult, {});
  }

  /** DELETE /admin/invitations/:id: cancels a pending invitation. */
  cancelInvitation(invitationId: string): Observable<void> {
    return this.delete(invitationId);
  }

  /** DELETE /admin/invitations/:id/delete: removes the invitation. */
  deleteInvitation(invitationId: string): Observable<void> {
    return this.delete(invitationId, { path: 'delete' });
  }
}

function toInvitationRequest(body: ProfileInvitationRequest): ProfileInvitationRequest {
  return omitUndefined<ProfileInvitationRequest>({
    email: body.email,
    firstName: body.firstName,
    lastName: body.lastName,
    invitationType: body.invitationType,
    organizationId: body.organizationId,
    companyName: body.companyName,
    skipEmail: body.skipEmail,
  });
}

function toCreateResponse(data: unknown): ProfileInvitationCreateResponse {
  const source = isRecord(data) ? data : {};
  const profile = isRecord(source['profile']) ? source['profile'] : {};
  return {
    invitation: isRecord(source['invitation']) ? new ProfileInvitation(source['invitation']) : null,
    organizationId: nullableString(source['organizationId']) ?? null,
    emailSent: source['emailSent'] === true,
    profileId: idString(profile['id']),
  };
}
